import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert, Check, Loader2, Eye } from 'lucide-react';
import { api } from '../lib/api';
import { Card } from '../components/Card';
import { HelperCard } from '../components/HelperCard';
import { PageSpinner } from '../components/Spinner';
import { PageWrapper } from '../components/PageWrapper';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';

// House language (12-portal-design-overhaul): borderless rows on a raised fill,
// separated by space + a quiet shadow. Severity reads through the dot + the
// label colour only — NO left-stripe, NO tinted wrapper border.
const SEVERITY_STYLES = {
  HIGH:   { dot: 'bg-red-400',   text: 'text-red-200' },
  MEDIUM: { dot: 'bg-amber-400', text: 'text-amber-200' },
  LOW:    { dot: 'bg-iron-400',  text: 'text-iron-200' },
};

export function AlertsInbox() {
  const qc = useQueryClient();
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['alerts', showAll ? 'all' : 'open'],
    queryFn: () => api.listAlerts(showAll ? 'all' : undefined),
  });

  const ackMut = useMutation({
    mutationFn: (id: string) => api.ackAlert(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['alerts'] }); },
  });

  if (isLoading) return <PageSpinner />;

  const alerts = data?.alerts ?? [];
  const openCount = alerts.filter(a => a.status === 'OPEN').length;

  return (
    <PageWrapper maxWidth="4xl" spacing="none">
      <div className={cn('mb-8 flex items-center justify-between gap-3', SETTLE)} style={settleDelay(0)}>
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-7 w-7 text-ink-secondary" />
          <div>
            <h1 className="text-2xl font-bold text-white">Alerts</h1>
            <p className="text-xs text-ink-meta">
              {openCount} open · triggered inline by actor activity patterns
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowAll(s => !s)}
          className={cn('flex items-center gap-1.5 rounded-md bg-white/[0.05] px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider text-ink-secondary transition-colors hover:bg-white/[0.08] hover:text-ink-primary', MORPH)}
        >
          <Eye className="h-3 w-3" strokeWidth={2.5} />
          {showAll ? 'Open only' : 'Show all'}
        </button>
      </div>

      <HelperCard
        id="alerts-intro"
        title="How alerts fire"
        intro="Every action evaluates its own risk at the moment it happens — no nightly scan. High-severity alerts also DM founders directly on Discord."
        points={[
          { text: 'Ghost-seat spam, mass member removals, rapid clan creation and fast application decisions are the baseline rules.' },
          { text: 'Ack an alert to clear it from the open list. Acked alerts stay for 90 days then auto-expire.' },
        ]}
        className={cn('mb-6', SETTLE)}
      />

      {alerts.length === 0 ? (
        <Card className="border-none text-center">
          <p className="text-sm text-ink-secondary">
            {showAll ? 'No alerts on record.' : 'No open alerts. Nice and quiet.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert, i) => {
            const style = SEVERITY_STYLES[alert.severity];
            const isAcked = alert.status === 'ACKNOWLEDGED';
            return (
              <div
                key={alert.id}
                className={cn(
                  'rounded-lg bg-white/[0.04] p-4 transition-opacity',
                  SETTLE,
                  isAcked && 'opacity-50'
                )}
                style={settleDelay(i + 1)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', style.dot, alert.severity === 'HIGH' && !isAcked && 'animate-pulse')} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={cn('font-display text-[10px] font-black uppercase tracking-[0.22em]', style.text)}>
                          {alert.severity}
                        </span>
                        <span className="font-display text-[10px] font-black uppercase tracking-wider text-iron-400">
                          · {alert.trigger}
                        </span>
                        {isAcked && (
                          <span className="rounded bg-white/[0.05] px-1.5 font-display text-[9px] font-black uppercase tracking-wider text-iron-400">
                            Acked
                          </span>
                        )}
                      </div>
                      <p className="mt-1 font-display text-sm font-black uppercase tracking-wide text-white">
                        {alert.description}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] text-iron-400">
                        <span>Actor: {alert.actorId}</span>
                        <span>Entity: {alert.entity}</span>
                        <span>{new Date(alert.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {!isAcked && (
                    <button
                      onClick={() => ackMut.mutate(alert.id)}
                      disabled={ackMut.isPending}
                      className={cn('flex shrink-0 items-center gap-1.5 rounded-md bg-emerald-500/15 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-emerald-200 transition-colors hover:bg-emerald-500/25 hover:text-white disabled:opacity-40', MORPH)}
                    >
                      {ackMut.isPending && ackMut.variables === alert.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                      ) : (
                        <Check className="h-3 w-3" strokeWidth={3} />
                      )}
                      Ack
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PageWrapper>
  );
}
